import { pairHasOpenIssue, useTaskActivitiesForServiceRequest } from '@commenda/api/tasks';
import { Button, Icons } from '@commenda/kakashi';
import {
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { getServiceRequestServiceId } from '@commenda/types';
import { useRouter } from 'next/router';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { getTaskLabel } from '../../task-labels';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { DefaultIncorporationTasks } from '../default-incorporation';

export const ApproveCompanyDetails = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest } = context;

  const router = useRouter();

  const { data: activityFeed } = useTaskActivitiesForServiceRequest(
    serviceRequest?.id
  );
  const taskWithWorkRequest = serviceRequest?.taskGroups
    ?.find((srtg) => srtg.id === task.taskGroupId)
    ?.tasks?.find(
      (srt) => srt.legacyServiceRequestTaskTaskId === DefaultIncorporationTasks.FILL_CORPORATION_DETAILS
    );
  const taskHasOpenIssue = pairHasOpenIssue(
    activityFeed?.activities,
    taskWithWorkRequest,
    task
  );

  const navigateToVerifyWorkRequest = () => {
    router.push(`/work-request/${taskWithWorkRequest?.workRequestId}/verify`);
  };

  const getLabel = (
    labelRole: TaskParty,
    status: TaskStatus
  ) =>
    getTaskLabel(
      getServiceRequestServiceId(serviceRequest),
      task.legacyServiceRequestTaskTaskId,
      labelRole,
      status,
      task.title
    ) ?? task.title;

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) =>
      ServiceRequestTaskIconConfig[taskHasOpenIssue ? TaskStatus.IN_PROGRESS : status],
    getStateConfig: (state) => {
      if (taskHasOpenIssue) {
        if (state.role === TaskParty.CLIENT) {
          return {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="Client fixing the reported issues"
                status={TaskStatus.IN_PROGRESS}
                timestamp={task.updatedAt}
              />
            ),
          };
        }
        if (state.role === TaskParty.AGENT) {
          return {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={navigateToVerifyWorkRequest}
              >
                View <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Client fixing the reported issues"
                status={TaskStatus.IN_PROGRESS}
                timestamp={task.updatedAt}
              />
            ),
          };
        }
      }

      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(
                  TaskParty.CLIENT,
                  TaskStatus.COMPLETED
                )}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(
                  TaskParty.CLIENT,
                  TaskStatus.TO_DO
                )}
              />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(
                  TaskParty.CLIENT,
                  TaskStatus.BACKLOG
                )}
              />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={navigateToVerifyWorkRequest}
                variant="primary"
              >
                Start <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(
                  TaskParty.AGENT,
                  TaskStatus.TO_DO
                )}
              />
            ),
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: (
              <Button
                size="sm"
                onClick={navigateToVerifyWorkRequest}
                variant="primary"
              >
                View <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(
                  TaskParty.AGENT,
                  TaskStatus.IN_PROGRESS
                )}
                status={TaskStatus.IN_PROGRESS}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled variant="primary">
                Start <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(
                  TaskParty.AGENT,
                  TaskStatus.BACKLOG
                )}
              />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={navigateToVerifyWorkRequest}
                >
                  View <Icons.ArrowNarrowRight className="w-4" />
                </Button>
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(
                  TaskParty.AGENT,
                  TaskStatus.COMPLETED
                )}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <ServiceRequestTaskCard
      task={task}
      state={{
        role,
        status: task.status,
      }}
      context={context}
      config={config}
      isTimelineView={isTimelineView}
      displayNumber={displayNumber}
    />
  );
};
